import { useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isNativeRuntime } from '@/lib/nativeRuntime';
import { Capacitor } from '@capacitor/core';
import despia from 'despia-native';

// Singleton guards — prevent concurrent or redundant registration across hook instances
let isRegistering = false;
let lastRegistrationAttempt = 0;
let registrationSuccessful = false;

const REGISTRATION_COOLDOWN_MS  = 15_000; // 15 s between top-level attempts
const MAX_REGISTRATION_RETRIES  = 3;
// FIX: More patient player_id polling — OneSignal can take 8-10 s on cold start.
// Original used 3 attempts × 1.5 s = 4.5 s max, which missed slow initializations.
const PLAYER_ID_POLL_ATTEMPTS   = 5;      // 5 attempts × 2 s = up to 10 s total
const PLAYER_ID_POLL_INTERVAL_MS = 2_000;

// FIX: Module-level pure helpers avoid creating new function references on every render.
const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * Retrieve OneSignal player_id with retry logic.
 * Tries sync property first, then async Despia command, repeating up to maxAttempts.
 */
const getPlayerIdWithRetry = async (maxAttempts = PLAYER_ID_POLL_ATTEMPTS): Promise<string | null> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      // 1. Synchronous property — fastest path, works if OneSignal already registered
      const syncId = despia.onesignalplayerid;
      if (syncId && typeof syncId === 'string' && syncId.length > 10) {
        console.log(`[PushSetup] player_id via sync (attempt ${attempt + 1}):`, syncId);
        return syncId;
      }

      // 2. Async Despia command — triggers native OneSignal registration if needed
      console.log(`[PushSetup] Async getonesignalplayerid (attempt ${attempt + 1}/${maxAttempts})...`);
      const asyncResult = await Promise.race([
        despia('getonesignalplayerid://'),
        wait(3_000).then(() => null),
      ]);

      // 3. Re-check sync after async — the async call may have populated the value
      const afterId = despia.onesignalplayerid;
      if (afterId && typeof afterId === 'string' && afterId.length > 10) {
        console.log(`[PushSetup] player_id after async call:`, afterId);
        return afterId;
      }

      if (asyncResult && typeof asyncResult === 'string' && asyncResult.length > 10) {
        console.log(`[PushSetup] player_id from async result:`, asyncResult);
        return asyncResult;
      }

      console.log(`[PushSetup] No player_id yet (attempt ${attempt + 1}/${maxAttempts})`);
      if (attempt < maxAttempts - 1) await wait(PLAYER_ID_POLL_INTERVAL_MS);

    } catch (err) {
      console.warn(`[PushSetup] player_id error (attempt ${attempt + 1}):`, err);
      if (attempt < maxAttempts - 1) await wait(1_000);
    }
  }
  return null;
};

/**
 * Hook to handle push notification setup for native apps.
 *
 * Flow:
 *   1. Request push permission via Despia
 *   2. Set OneSignal external_id to Supabase user_id
 *   3. Retrieve real player_id with async retry (up to ~10 s)
 *   4. Store registration via edge function (bypasses RLS)
 *   5. Direct DB upsert as belt-and-suspenders backup
 */
export const usePushNotificationSetup = () => {
  // FIX: useMemo so isNativeRuntime() is not called on every render
  const isNative = useMemo(() => isNativeRuntime(), []);

  // FIX: Track successful registration, not just attempted.
  // Original used hasAttemptedRef set to true BEFORE the attempt, so a
  // failed registration was never retried on re-mount.
  const hasRegisteredRef = useRef(false);
  const userIdRef        = useRef<string | null>(null);
  const retryCountRef    = useRef(0);
  const retryTimeoutRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  // FIX: mountedRef for the retry timeout callback (which fires outside the effect lifecycle)
  const mountedRef       = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const registerForPush = useCallback(async (userId: string, isRetry = false): Promise<boolean> => {
    const now = Date.now();

    // FIX: Skip only if registration for this specific user already succeeded.
    // Previously this check used hasAttemptedRef (set before the attempt), so
    // a failed first registration would never be reattempted on re-mount.
    if (!isRetry && hasRegisteredRef.current && userIdRef.current === userId) {
      console.log('[PushSetup] Already registered for user, skipping');
      return true;
    }

    if (!isRetry && (isRegistering || (now - lastRegistrationAttempt) < REGISTRATION_COOLDOWN_MS)) {
      console.log('[PushSetup] Registration in progress or on cooldown, skipping');
      return registrationSuccessful;
    }

    isRegistering = true;
    lastRegistrationAttempt = now;

    try {
      console.log('[PushSetup] === STARTING PUSH REGISTRATION ===', { userId, isRetry });

      // Step 1: Request push permission
      console.log('[PushSetup] Step 1: Requesting push permission...');
      try {
        await Promise.race([
          despia('checkNativePushPermissions://'),
          wait(5_000).then(() => { throw new Error('Permission timeout'); }),
        ]);
        console.log('[PushSetup] ✅ Push permission requested');
      } catch (permError) {
        // Non-fatal — permission may already be granted
        console.warn('[PushSetup] ⚠️ Permission check issue (may already be granted):', permError);
      }

      await wait(300);

      // Step 2: Set OneSignal external_id to Supabase user_id
      console.log('[PushSetup] Step 2: Setting OneSignal external_id...');
      try {
        await Promise.race([
          despia(`setonesignalplayerid://?user_id=${userId}`),
          wait(10_000).then(() => { throw new Error('OneSignal external_id timeout'); }),
        ]);
        console.log('[PushSetup] ✅ OneSignal external_id set');
      } catch (osError) {
        // Non-fatal — proceed to retrieve player_id anyway
        console.error('[PushSetup] ❌ setonesignalplayerid failed:', osError);
      }

      // Step 3: Wait then retrieve player_id with patient retry
      console.log(`[PushSetup] Step 3: Retrieving player_id (up to ${PLAYER_ID_POLL_ATTEMPTS * (PLAYER_ID_POLL_INTERVAL_MS / 1000)}s)...`);
      await wait(2_000); // Give OneSignal time to process the external_id

      const playerId = await getPlayerIdWithRetry(PLAYER_ID_POLL_ATTEMPTS);
      console.log('[PushSetup] Final player_id:', playerId ?? 'null');

      // FIX: Never store a fake player_id. The original fell back to
      // `external_${userId}` when no real ID was available — this poisoned
      // the database with an invalid OneSignal subscriber and caused all
      // push sends to silently fail for that user. If we can't get a real
      // player_id, schedule a retry instead.
      if (!playerId) {
        console.warn('[PushSetup] ⚠️ No real player_id retrieved — scheduling retry');
        registrationSuccessful = false;

        if (retryCountRef.current < MAX_REGISTRATION_RETRIES && mountedRef.current) {
          retryCountRef.current++;
          const backoffMs = Math.min(5_000 * Math.pow(2, retryCountRef.current - 1), 30_000);
          console.log(`[PushSetup] Retry ${retryCountRef.current}/${MAX_REGISTRATION_RETRIES} in ${backoffMs}ms`);
          retryTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current && userIdRef.current === userId) {
              registerForPush(userId, true);
            }
          }, backoffMs);
        }
        return false;
      }

      // FIX: Detect Android vs iOS instead of hardcoding 'ios'
      const deviceType = (typeof Capacitor !== 'undefined' && Capacitor.getPlatform() === 'android')
        ? 'android'
        : 'ios';

      // Step 4: Store via edge function (bypasses RLS)
      console.log('[PushSetup] Step 4: Storing registration via edge function...');
      const { data: sessionData } = await supabase.auth.getSession();

      if (sessionData?.session?.access_token) {
        try {
          const { data: regResult, error: regError } = await supabase.functions.invoke(
            'register-push-notification',
            { body: { player_id: playerId, device_type: deviceType } },
          );

          if (regError) {
            console.error('[PushSetup] ❌ Edge function registration failed:', regError);
          } else {
            console.log('[PushSetup] ✅ Edge function registration successful:', regResult);
          }
        } catch (invokeErr) {
          console.error('[PushSetup] ❌ Edge function invoke failed:', invokeErr);
        }
      }

      // Step 5: Direct DB upsert as belt-and-suspenders backup
      const { error: dbError } = await supabase
        .from('push_notification_registrations')
        .upsert(
          {
            user_id:     userId,
            player_id:   playerId,
            device_type: deviceType,
            updated_at:  new Date().toISOString(),
          },
          { onConflict: 'user_id,player_id' },
        );

      if (dbError) {
        console.warn('[PushSetup] Direct DB upsert failed (edge function may have succeeded):', dbError);
      } else {
        console.log('[PushSetup] ✅ Direct DB upsert successful');
      }

      console.log('[PushSetup] === PUSH REGISTRATION COMPLETE ===', { userId, playerId, deviceType });

      registrationSuccessful       = true;
      hasRegisteredRef.current     = true;  // FIX: only set true on success
      retryCountRef.current        = 0;
      return true;

    } catch (error) {
      console.error('[PushSetup] ❌ Registration failed:', error);
      registrationSuccessful = false;

      if (retryCountRef.current < MAX_REGISTRATION_RETRIES && mountedRef.current) {
        retryCountRef.current++;
        const backoffMs = Math.min(5_000 * Math.pow(2, retryCountRef.current - 1), 30_000);
        console.log(`[PushSetup] Retry ${retryCountRef.current}/${MAX_REGISTRATION_RETRIES} in ${backoffMs}ms`);
        retryTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current && userIdRef.current === userId) {
            registerForPush(userId, true);
          }
        }, backoffMs);
      }

      return false;
    } finally {
      isRegistering = false;
    }
  }, []);

  const unregisterPush = useCallback(async () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    try {
      console.log('[PushSetup] Unregistering push notifications...');
      // FIX: Clear OneSignal external_id so this device is no longer linked to
      // the signed-out user. Original only cleared local state — the device
      // stayed linked in OneSignal and could still receive that user's pushes.
      await Promise.race([
        despia('removeonesignalexternalid://'),
        wait(3_000).then(() => null),
      ]).catch(() => {}); // Best-effort — don't block sign-out on failure
    } catch (error) {
      console.error('[PushSetup] Unregistration Despia call failed:', error);
    } finally {
      userIdRef.current            = null;
      hasRegisteredRef.current     = false;
      registrationSuccessful       = false;
      retryCountRef.current        = 0;
    }
  }, []);

  useEffect(() => {
    if (!isNative) {
      console.log('[PushSetup] Not native, skipping');
      return;
    }

    let mounted = true;

    const setupPush = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.warn('[PushSetup] Session check failed:', error);
        }
        if (session?.user?.id && mounted) {
          if (userIdRef.current !== session.user.id) {
            userIdRef.current        = session.user.id;
            hasRegisteredRef.current = false; // New user — clear success flag
          }
          await registerForPush(session.user.id);
        } else if (mounted) {
          // Guest user — request push permission so the device is registered
          // with OneSignal. This allows segment/broadcast notifications to
          // reach guest users who downloaded the app but haven't signed up.
          console.log('[PushSetup] No session — requesting guest push permission');
          try {
            await Promise.race([
              despia('checkNativePushPermissions://'),
              wait(5_000).then(() => { throw new Error('Guest permission timeout'); }),
            ]);
            console.log('[PushSetup] ✅ Guest push permission requested');
          } catch (permErr) {
            console.warn('[PushSetup] Guest permission request issue:', permErr);
          }
        }
      } catch (err) {
        console.error('[PushSetup] Setup failed:', err);
      }
    };

    setupPush();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[PushSetup] Auth state changed:', event);

      if (event === 'SIGNED_IN' && session?.user?.id && mounted) {
        if (userIdRef.current !== session.user.id) {
          userIdRef.current        = session.user.id;
          hasRegisteredRef.current = false;
        }
        await registerForPush(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        await unregisterPush();
      }
    });

    // Re-register on app foreground — but only if not already successfully
    // registered. The cooldown also guards against rapid re-fire.
    // FIX: Original attempted re-registration on every visibility-visible
    // event even when already registered, causing unnecessary Despia calls.
    const handleVisibility = async () => {
      if (
        document.visibilityState === 'visible' &&
        userIdRef.current          &&
        !hasRegisteredRef.current  &&
        mounted
      ) {
        await registerForPush(userIdRef.current);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibility);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [isNative, registerForPush, unregisterPush]);

  return {
    registerForPush,
    unregisterPush,
    isNative,
    isRegistered: registrationSuccessful,
  };
};
